import {
    DEFAULT_WIDGET_SETTINGS,
    SAMPLE_REVIEWS,
    SPOTLIGHT_DURATIONS,
    SPOTLIGHT_FIRST_DELAYS,
    SPOTLIGHT_GAPS,
    type PreviewReview,
    type WidgetSettings,
    type WidgetType,
} from '@flexbyt/shared';
import { WidgetPreview } from '@flexbyt/widget-preview';

/**
 * A real ReviewsJet widget, on a marketing page.
 *
 * This is the thin seam between the site and the renderer, and it is
 * deliberately thin: it picks the reviews and passes settings through. Every
 * pixel of the widget itself is drawn by `@flexbyt/widget-preview`, which is
 * the same code the dashboard's editor draws with — so what somebody sees here
 * before signing up is what they get afterwards.
 *
 * The alternative, which the brief rules out and which every marketing site
 * drifts into, is a prettier review card written for the homepage. It always
 * ends up nicer than the product, and the first thing a new customer sees in
 * their dashboard is a downgrade from the thing that sold them.
 *
 * ===========================================================================
 * Marketing defaults live here, and there are exactly two of them
 * ===========================================================================
 * Both are presentation choices for *this website* rather than claims about the
 * product, and both are documented at `showcaseDefaults` below: the branding
 * line, and the spotlight's timings. Any caller can pass its own value and win.
 *
 * Worth keeping in mind if this component is ever used somewhere the
 * distinction matters — the branding line is a real thing a free widget draws
 * on a customer's site, and the pricing page is where that is described.
 */

/**
 * The spotlight's timings, for a page that is showing it rather than running it.
 *
 * ===========================================================================
 * The product's defaults are right on a customer's site and wrong here
 * ===========================================================================
 * `DEFAULT_WIDGET_SETTINGS` gives the spotlight five seconds of quiet, eight
 * seconds on screen and twenty-five seconds of nothing, and the shared package
 * explains exactly why: it puts about two reviews in front of a visitor per
 * minute while leaving the page alone for three quarters of it. On somebody's
 * own website that restraint is the entire point — a card that is up more than
 * it is down stops being a spotlight and becomes an advert.
 *
 * On this site the widget is the subject, not an interruption. Somebody scrolls
 * to the spotlight row in the gallery, or opens its generator, in order to look
 * at it — and with the product's timings they can land on a twenty-five second
 * gap and see an empty box. There is nothing to tell them it is a widget that
 * comes and goes rather than a widget that is broken, so they leave.
 *
 * ===========================================================================
 * Slowest gap, longest stay, and nothing invented
 * ===========================================================================
 * Every value here is one the product's own picker offers — the first entry of
 * `SPOTLIGHT_FIRST_DELAYS` and `SPOTLIGHT_GAPS`, the last of
 * `SPOTLIGHT_DURATIONS`. That matters more than shaving another second off:
 * a showcase configured with numbers no customer could choose is demonstrating
 * something they cannot have. These are the extremes of what is on offer, and a
 * customer who wants the widget this present can set exactly this.
 *
 * The result is two seconds to the first card and then twenty seconds visible
 * out of every thirty, rather than eight out of thirty-three. Read as "how long
 * before I see anything", it goes from up to thirty-three seconds to at most
 * twelve.
 *
 * Read from the option lists rather than typed as `2`, `20`, `10`, so that a
 * product decision to change what the picker offers cannot leave this file
 * quietly out of range.
 */
const SHOWCASE_SPOTLIGHT: Partial<WidgetSettings> = {
  spotlightFirstDelay: SPOTLIGHT_FIRST_DELAYS[0],
  spotlightDuration: SPOTLIGHT_DURATIONS[SPOTLIGHT_DURATIONS.length - 1],
  spotlightGap: SPOTLIGHT_GAPS[0],
};

/**
 * The presentation defaults this site applies before any caller's settings.
 *
 * Exported because the free builder and the widget playground draw
 * `WidgetPreview` directly rather than going through `WidgetShowcase` — they
 * have their own settings pipeline for the controls they offer — and a
 * spotlight that behaved differently in the gallery and in its own generator
 * would be the drift this whole file exists to prevent.
 *
 * Deliberately *not* merged into the builder's `overrides`, which is the diff
 * that travels into signup. These are decisions about this website, not about
 * the widget somebody is building: a visitor who never touched the timings
 * should arrive in the dashboard with the product's defaults, not with the
 * marketing site's.
 */
export function showcaseDefaults(type: WidgetType): Partial<WidgetSettings> {
  return {
    /*
      Every widget on this site is being shown *on reviewsjet.com*, where a line
      of small print reading "Powered by ReviewsJet" under a ReviewsJet widget on
      a ReviewsJet page tells the reader nothing they do not already know.
    */
    removeBranding: true,
    ...(type === 'spotlight' ? SHOWCASE_SPOTLIGHT : {}),
  };
}

export function WidgetShowcase({
  type,
  reviews = SAMPLE_REVIEWS,
  settings,
  className,
}: {
  type: WidgetType;
  /**
   * Real reviews when the page has them.
   *
   * Defaults to the shared sample set — the same twenty the dashboard shows an
   * account with nothing imported yet, uneven in length on purpose so masonry
   * looks like masonry rather than a grid. They are drawn illustrations and
   * invented names, and anything presenting them as ReviewsJet's own customers
   * would be fabricating testimonials.
   */
  reviews?: PreviewReview[];
  settings?: Partial<WidgetSettings>;
  className?: string;
}) {
  return (
    <WidgetPreview
      type={type}
      reviews={reviews}
      settings={{
        ...DEFAULT_WIDGET_SETTINGS,
        // Before the caller's settings, so a caller that passes
        // `removeBranding: false` — or its own spotlight timings — still wins.
        ...showcaseDefaults(type),
        ...settings,
      }}
      className={className}
    />
  );
}
